import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../contexts/ModeContext';
import { ModeIndicator } from './ModeIndicator';
import { GabryxFooter } from './GabryxBranding';
import { ImpersonationBanner } from './ImpersonationBanner';
import { FeedbackFloatingButton } from './FeedbackFloatingButton';
import {
  House,
  Buildings,
  ClipboardText,
  CalendarBlank,
  ShieldCheck,
  User,
  SignOut,
  List,
  Binoculars,
  FolderOpen,
  Presentation,
  CreditCard,
  Notepad,
  AddressBook,
  Notebook,
  X,
  Gear,
  Bell,
  Flag,
  UserSwitch,
  Sparkle,
  HardHat,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function NavItem({ to, icon: Icon, label, activeClass, testId, onClick }) {
  return (
    <NavLink to={to} data-testid={testId} onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-150 text-sm ${
          isActive ? activeClass : 'text-[#4B5563] hover:bg-gray-100'
        }`
      }>
      <Icon size={18} weight="bold" />
      <span className="font-medium">{label}</span>
    </NavLink>
  );
}

function SecondaryNavItem({ to, icon: Icon, label, testId, onClick }) {
  return (
    <NavLink to={to} data-testid={testId} onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-150 text-sm ${
          isActive ? 'bg-gray-200 text-[#111827]' : 'text-[#4B5563] hover:bg-gray-100'
        }`
      }>
      <Icon size={18} weight="bold" />
      <span className="font-medium">{label}</span>
    </NavLink>
  );
}

const opcNavItems = [
  { path: '/dashboard', label: 'Accueil', icon: House },
  { path: '/chantiers', label: 'Chantiers', icon: Buildings },
  { path: '/réserves', label: 'Réserves', icon: ClipboardText },
  { path: '/visites', label: 'Visites de chantier', icon: HardHat },
  { path: '/planning', label: 'Planning', icon: CalendarBlank },
  { path: '/documents-opc', label: 'Documents', icon: Notepad },
  { path: '/annuaire', label: 'Annuaire', icon: AddressBook },
];

const cspsNavItems = [
  { path: '/dashboard', label: 'Accueil', icon: House },
  { path: '/csps/chantiers', label: 'Chantiers CSPS', icon: Buildings },
  { path: '/sécurité', label: 'Observations', icon: ShieldCheck },
  { path: '/csps/visites', label: 'Visites', icon: Binoculars },
  { path: '/csps/inspections', label: 'Inspections', icon: ClipboardText },
  { path: '/csps/documents', label: 'Documents CSPS', icon: FolderOpen },
  { path: '/journal', label: 'Journal', icon: Notebook },
  { path: '/annuaire', label: 'Annuaire', icon: AddressBook },
];

const opcMobileItems = [
  { path: '/dashboard', label: 'Accueil', icon: House },
  { path: '/chantiers', label: 'Chantiers', icon: Buildings },
  { path: '/visites', label: 'Visites', icon: HardHat },
  { path: '/planning', label: 'Planning', icon: CalendarBlank },
  { path: '/documents-opc', label: 'Documents', icon: Notepad },
];

const cspsMobileItems = [
  { path: '/dashboard', label: 'Accueil', icon: House },
  { path: '/csps/chantiers', label: 'Chantiers', icon: Buildings },
  { path: '/csps/inspections', label: 'Inspections', icon: ClipboardText },
  { path: '/csps/documents', label: 'Documents', icon: FolderOpen },
  { path: '/journal', label: 'Journal', icon: Notebook },
];

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { mode, isOPC, isCSPS, hasOPC, hasCSPS, canSwitch } = useMode();
  const navigate = useNavigate();
  const [companySettings, setCompanySettings] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chantixAiEnabled, setChantixAiEnabled] = useState(false);

  const activeNavItems = isOPC ? opcNavItems : cspsNavItems;
  const activeMobileItems = isOPC ? opcMobileItems : cspsMobileItems;
  const accentColor = isOPC ? '#0055FF' : '#FF6B00';

  useEffect(() => {
    axios.get(`${API}/settings`, { withCredentials: true })
      .then(res => setCompanySettings(res.data))
      .catch(() => {});
  }, [setCompanySettings]);

  useEffect(() => {
    axios.get(`${API}/chantix-ai/status`, { withCredentials: true })
      .then(res => setChantixAiEnabled(!!res.data?.enabled))
      .catch(() => setChantixAiEnabled(false));
  }, [user?.id]);

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    await logout();
    navigate('/login');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const logoUrl = companySettings?.logo_url
    ? `${process.env.REACT_APP_BACKEND_URL}${companySettings.logo_url}`
    : '/chantix-logo.png';
  const companyName = companySettings?.company_name || 'CHANTIX';

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <ImpersonationBanner user={user} />
      {/* Desktop Sidebar */}
      <aside className="chantix-sidebar" data-testid="desktop-sidebar">
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-[#E5E7EB]">
            <div className="flex items-center justify-center" data-testid="app-logo">
              <img src={logoUrl} alt={companyName}
                className="h-14 w-auto object-contain max-w-[180px]" data-testid="app-logo-img" />
            </div>
          </div>
          <div className="py-1.5">
            <ModeIndicator variant="full" />
          </div>
          <nav className="flex-1 px-3 py-1.5 space-y-0.5 overflow-y-auto">
            {activeNavItems.map((item) => (
              <NavItem key={item.path} to={item.path} icon={item.icon} label={item.label}
                activeClass={`text-white ${isOPC ? 'bg-[#0055FF]' : 'bg-[#FF6B00]'}`}
                testId={`nav-${item.path.replace(/\//g, '-').slice(1)}`} />
            ))}
            <div className="pt-2 mt-2 border-t border-[#E5E7EB]">
              <SecondaryNavItem to="/profil" icon={User} label="Profil" testId="nav-profil" />
              {user?.role === 'admin' && (
                <SecondaryNavItem to="/admin/users" icon={UserSwitch} label="Impersonation" testId="nav-impersonation" />
              )}
              {chantixAiEnabled && (
                <NavLink to="/chantix-ai" data-testid="nav-chantix-ai"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-150 text-sm ${
                      isActive
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm'
                        : 'text-indigo-700 hover:bg-indigo-50'
                    }`
                  }>
                  <Sparkle size={18} weight="fill" />
                  <span className="font-medium flex-1">AXEL</span>
                  <span className="text-[9px] font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded uppercase">Beta</span>
                </NavLink>
              )}
              <SecondaryNavItem to="/settings" icon={Gear} label="Paramètres" testId="nav-settings" />
              {user?.role === 'admin' && (
                <SecondaryNavItem to="/admin/plans" icon={CreditCard} label="Plans tarifaires" testId="nav-presentation" />
              )}
              {user?.role === 'admin' && (
                <SecondaryNavItem to="/admin/notifications" icon={Bell} label="Notifications" testId="nav-admin-notifications" />
              )}
              {user?.role === 'admin' && (
                <SecondaryNavItem to="/admin/pilots" icon={Flag} label="Phase Pilote" testId="nav-admin-pilots" />
              )}
              <SecondaryNavItem to="/subscription" icon={CreditCard} label="Abonnement" testId="nav-subscription" />
            </div>
          </nav>
          <div className="px-4 py-2 border-t border-[#E5E7EB]">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full bg-[#0055FF] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-xs truncate">{user?.name || 'Utilisateur'}</p>
                <p className="text-[10px] text-[#9CA3AF] truncate">{user?.email}</p>
              </div>
            </div>
            <button onClick={handleLogout} data-testid="logout-btn"
              className="flex items-center gap-2 w-full px-3 py-1.5 text-[#4B5563] hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
              <SignOut size={16} />
              <span className="text-xs font-medium">Déconnexion</span>
            </button>
            <GabryxFooter variant="sidebar" />
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E5E7EB] flex items-center justify-between px-4 z-40">
        <img src={logoUrl} alt={companyName}
          className="h-11 w-auto object-contain max-w-[140px]" data-testid="mobile-logo-img" />
        <div className="flex items-center gap-2">
          <ModeIndicator variant="compact" />
          <button onClick={() => setMobileMenuOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-md" data-testid="mobile-menu-btn">
            <List size={24} className="text-[#111827]" />
          </button>
        </div>
      </header>

      {/* Mobile Slide-out Menu */}
      {mobileMenuOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[9999] md:hidden" onClick={closeMobileMenu} data-testid="mobile-menu-overlay" />
          <div className="fixed top-0 right-0 w-72 bg-white z-[10000] md:hidden shadow-2xl flex flex-col"
            style={{ animation: 'slideInRight 0.2s ease-out', bottom: '0', height: '100dvh' }}
            data-testid="mobile-menu-drawer">
            {/* Menu header */}
            <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB] flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold"
                  style={{ backgroundColor: accentColor }}>
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-[#111827] truncate">{user?.name || 'Utilisateur'}</p>
                  <p className="text-xs text-[#9CA3AF] truncate">{user?.email}</p>
                </div>
              </div>
              <button onClick={closeMobileMenu} className="p-2 hover:bg-gray-100 rounded-md" data-testid="mobile-menu-close">
                <X size={20} className="text-[#4B5563]" />
              </button>
            </div>

            {/* Menu navigation */}
            <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1 min-h-0 pb-8">
              {activeNavItems.map((item) => (
                <NavItem key={item.path} to={item.path} icon={item.icon} label={item.label}
                  activeClass={`text-white ${isOPC ? 'bg-[#0055FF]' : 'bg-[#FF6B00]'}`}
                  testId={`drawer-${item.path.replace(/\//g, '-').slice(1)}`}
                  onClick={closeMobileMenu} />
              ))}

              <div className="pt-3 mt-3 border-t border-[#E5E7EB]">
                <SecondaryNavItem to="/profil" icon={User} label="Profil" testId="drawer-profil" onClick={closeMobileMenu} />
                {user?.role === 'admin' && (
                  <SecondaryNavItem to="/admin/users" icon={UserSwitch} label="Impersonation" testId="drawer-impersonation" onClick={closeMobileMenu} />
                )}
                {chantixAiEnabled && (
                  <NavLink to="/chantix-ai" data-testid="drawer-chantix-ai" onClick={closeMobileMenu}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-md transition-all text-sm ${
                        isActive
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                          : 'text-indigo-700 hover:bg-indigo-50'
                      }`
                    }>
                    <Sparkle size={18} weight="fill" />
                    <span className="font-medium flex-1">AXEL</span>
                    <span className="text-[9px] font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded uppercase">Beta</span>
                  </NavLink>
                )}
                <SecondaryNavItem to="/settings" icon={Gear} label="Paramètres" testId="drawer-settings" onClick={closeMobileMenu} />
                <SecondaryNavItem to="/subscription" icon={CreditCard} label="Abonnement" testId="drawer-subscription" onClick={closeMobileMenu} />
                {user?.role === 'admin' && (
                  <SecondaryNavItem to="/admin/plans" icon={CreditCard} label="Plans tarifaires" testId="drawer-admin" onClick={closeMobileMenu} />
                )}
                {user?.role === 'admin' && (
                  <SecondaryNavItem to="/admin/notifications" icon={Bell} label="Notifications" testId="drawer-admin-notifications" onClick={closeMobileMenu} />
                )}
                {user?.role === 'admin' && (
                  <SecondaryNavItem to="/admin/pilots" icon={Flag} label="Phase Pilote" testId="drawer-admin-pilots" onClick={closeMobileMenu} />
                )}
              </div>

              <div className="pt-3 mt-3 border-t border-[#E5E7EB]">
                <button onClick={handleLogout} data-testid="drawer-logout-btn"
                  className="flex items-center gap-3 w-full px-3 py-2.5 text-red-600 hover:bg-red-50 rounded-md transition-colors">
                  <SignOut size={18} weight="bold" />
                  <span className="text-sm font-semibold">Déconnexion</span>
                </button>
              </div>
            </nav>
          </div>
        </>
      )}

      {/* Main Content */}
      <main className="chantix-main pt-16 md:pt-0 min-h-screen" data-testid="main-content">
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="chantix-bottom-nav" data-testid="mobile-nav">
        {activeMobileItems.map((item) => (
          <NavLink key={item.path} to={item.path}
            data-testid={`mobile-nav-${item.path.replace(/\//g, '-').slice(1)}`}
            className={({ isActive }) => `chantix-bottom-nav-item ${isActive ? 'active' : ''}`}
            style={({ isActive }) => isActive ? { color: accentColor } : {}}>
            <item.icon size={24} weight="regular" />
            <span className="text-xs mt-1">{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
      <FeedbackFloatingButton user={user} />
    </div>
  );
}
